import { useState } from "react";
import { Sparkles, ChevronDown, Repeat, ListPlus, X as XIcon } from "lucide-react";

export function ModelSelect({ models, model, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button data-testid="model-selector-dropdown" onClick={() => setOpen((o) => !o)}
        className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5 text-blue-600" /> {model.label} <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 left-0 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-52 z-30 max-h-64 overflow-y-auto cadence-scroll">
          {models.map((m) => (
            <button key={m.provider + m.id} data-testid={`model-option-${m.id}`} onClick={() => { onChange(m); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center justify-between">
              <span>{m.label}</span>
              <span className="text-[10px] text-gray-400 uppercase">{m.provider}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function SendModeControl({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button data-testid="send-mode-button" onClick={() => setOpen((o) => !o)} title="Default send behavior"
        className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 inline-flex items-center gap-1.5">
        {value === "interrupt" ? <Repeat className="w-3.5 h-3.5" /> : <ListPlus className="w-3.5 h-3.5" />}
        {value === "interrupt" ? "Interrupt" : "Queue"}
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 left-0 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-64 z-30">
          <div className="px-3 py-1.5 label-caps">Default send</div>
          {[
            { id: "interrupt", icon: Repeat, label: "Interrupt", hint: "Enter stops the run & starts new · ⌘↵ queues" },
            { id: "queue", icon: ListPlus, label: "Queue", hint: "Enter queues after the run · ⌘↵ interrupts" },
          ].map((o) => (
            <button key={o.id} data-testid={`send-mode-${o.id}`} onClick={() => { onChange(o.id); setOpen(false); }}
              className={`w-full text-left px-3 py-2 hover:bg-gray-50 flex flex-col ${value === o.id ? "bg-gray-50" : ""}`}>
              <span className="text-xs font-600 text-gray-800 inline-flex items-center gap-1.5"><o.icon className="w-3.5 h-3.5" /> {o.label}</span>
              <span className="text-[11px] text-gray-400 mt-0.5">{o.hint}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function GoalModal({ onClose, onSubmit, initial }) {
  const [g, setG] = useState(initial || "");
  const [auto, setAuto] = useState(true);
  const [autoMerge, setAutoMerge] = useState(false);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div data-testid="goal-modal" className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-700 text-lg flex items-center gap-2"><Sparkles className="w-4.5 h-4.5 text-blue-600" /> Parallel build</h3>
          <button data-testid="modal-close" onClick={onClose} className="text-gray-400 hover:text-gray-900 p-1"><XIcon className="w-4 h-4" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-3">Describe a goal. Cadence's orchestrator splits it into independent branches and runs an agent on each — in parallel.</p>
        {initial && (
          <div data-testid="goal-suggestion-banner" className="mb-3 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Suggested first build for your imported repo — edit or run as-is.
          </div>
        )}
        <textarea data-testid="goal-input" value={g} onChange={(e) => setG(e.target.value)} rows={3}
          placeholder="e.g. Add a navbar, a footer, and a dark-mode toggle"
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
        <div className="flex items-center justify-between mt-3">
          <div className="text-sm text-gray-700">
            Auto-apply changes
            <span className="block text-xs text-gray-400">{auto ? "Agents commit within their branch — no per-file approval." : "Each file change waits for your approval."}</span>
          </div>
          <button type="button" data-testid="goal-autoapply-toggle" onClick={() => setAuto((a) => !a)}
            className={`w-10 h-5 rounded-full p-0.5 flex items-center transition-colors ${auto ? "bg-blue-600 justify-end" : "bg-gray-300 justify-start"}`}>
            <span className="w-4 h-4 bg-white rounded-full shadow-sm" />
          </button>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div className="text-sm text-gray-700">
            Auto-merge when verified
            <span className="block text-xs text-gray-400">{autoMerge ? "Chips that pass tests merge automatically — provision & deploy still wait for approval." : "Verified chips wait for you to click Merge."}</span>
          </div>
          <button type="button" data-testid="goal-automerge-toggle" onClick={() => setAutoMerge((a) => !a)}
            className={`w-10 h-5 rounded-full p-0.5 flex items-center transition-colors ${autoMerge ? "bg-blue-600 justify-end" : "bg-gray-300 justify-start"}`}>
            <span className="w-4 h-4 bg-white rounded-full shadow-sm" />
          </button>
        </div>
        <button data-testid="goal-submit" onClick={() => onSubmit(g, { autoApply: auto, autoMerge })} disabled={!g.trim()}
          className="mt-4 w-full bg-black text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-900 disabled:opacity-50 inline-flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4" /> Decompose & run in parallel
        </button>
      </div>
    </div>
  );
}
